import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import U2000CheckBoxBase from '../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase';
import { NS } from '../../U3129';
import { U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from '../../tools/u3129ReadonlyTableCellClass';
import {
  U3129_GRID_COMPACT_ROW_HOUR_TD_BASE,
  U3129_HONORARIUM_CLICKABLE_PARENT_ROW_CLASS,
  U3129_HONORARIUM_COLLAPSED_PARENT_ROW_CLASS,
  U3129_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS,
  U3129_HONORARIUM_EXPANDED_PARENT_ROW_CLASS,
  U3129_HONORARIUM_GUTTER_TD_CLASS,
  U3129_HONORARIUM_META_COLUMN_CELL_X,
  U3129_HONORARIUM_TASK_ORPHAN_HEADER_CLASS,
} from '../../tools/u3129TimesheetGridCompactStyles';
import type { U2000TimesheetWeekDay } from '../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import {
  U3129_HONORARIUM_NO_PROJECT_KEY,
  type U3129HonorariumProjectGroup,
  u3129HonorariumProjectHeaderLabels,
} from '../../tools/u3129GroupHonorariumForDisplay';
import { u3129ProjectWorkNatureFromRow } from '../../tools/u3129HonorariumWorkNatureDisplay';
import type { U3129HonorariumGroupCheckState } from '../../tools/u3129HonorariumProjectSelection';
import U3129HonorariumCoherenceIcon from './U3129HonorariumCoherenceIcon';

export interface U3129HonorariumProjectHeaderRowProps {
  group: U3129HonorariumProjectGroup;
  expanded: boolean;
  onToggle: () => void;
  visibleDays: readonly U2000TimesheetWeekDay[];
  showSubProjectColumn: boolean;
  isFirstProject: boolean;
  projectColClass: string;
  customerColClass: string;
  naturesColClass: string;
  subprojectColClass: string;
  hourColClass: string;
  formatDayHours: (value: number) => string;
  formatTotalHours: (total: number) => string;
  panelId: string;
  showSelectionCheckbox?: boolean;
  groupCheckState?: U3129HonorariumGroupCheckState;
  onToggleGroupCheck?: () => void;
  /** BL-0688 — avertissement métier (projet inactif, etc.). */
  projectCoherenceMessage?: string;
}

const U3129HonorariumProjectHeaderRow: React.FC<U3129HonorariumProjectHeaderRowProps> = ({
  group,
  expanded,
  onToggle,
  visibleDays,
  showSubProjectColumn,
  isFirstProject,
  projectColClass,
  customerColClass,
  naturesColClass,
  subprojectColClass,
  hourColClass,
  formatDayHours,
  formatTotalHours,
  panelId,
  showSelectionCheckbox = false,
  groupCheckState = 'none',
  onToggleGroupCheck,
  projectCoherenceMessage = '',
}) => {
  const { t } = useTranslation();
  const { projectLabel, clientLabel } = u3129HonorariumProjectHeaderLabels(group.sampleRow);
  const noProject = group.projectKey === U3129_HONORARIUM_NO_PROJECT_KEY;
  const displayProject =
    noProject
      ? t('lblHonorariumNoProjectGroup', { ns: NS, defaultValue: 'Sans projet' })
      : projectLabel;
  const naturesLabel = noProject ? '' : u3129ProjectWorkNatureFromRow(group.sampleRow);
  const canSelectGroup = showSelectionCheckbox && group.allLines.length > 0;

  const expandFromRow = (event: React.MouseEvent) => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <tr
      className={`${U3129_HONORARIUM_CLICKABLE_PARENT_ROW_CLASS} border-b ${expanded ? `border-blue-100 ${U3129_HONORARIUM_EXPANDED_PARENT_ROW_CLASS}` : `border-gray-200 ${U3129_HONORARIUM_COLLAPSED_PARENT_ROW_CLASS}`} ${isFirstProject ? '' : 'border-t-2 border-t-gray-300'}`}
    >
      <td className={`${U3129_HONORARIUM_GUTTER_TD_CLASS} ${expanded ? U3129_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS : ''}`}>
        <div className="relative flex items-center justify-center gap-0.5">
          {projectCoherenceMessage ? (
            <span
              className="relative z-20 shrink-0"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <U3129HonorariumCoherenceIcon message={projectCoherenceMessage} />
            </span>
          ) : null}
          {canSelectGroup ? (
            <span
              className="relative z-20 inline-flex shrink-0"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <U2000CheckBoxBase
                fieldName={`u3129-honorarium-project-check-${group.projectKey}`}
                label=""
                compact
                value={groupCheckState === 'all'}
                indeterminate={groupCheckState === 'partial'}
                ariaLabel={t('btnSelectHonorariumProject', {
                  ns: NS,
                  defaultValue: 'Sélectionner le projet {{project}}',
                  project: displayProject,
                })}
                setValue={() => onToggleGroupCheck?.()}
              />
            </span>
          ) : null}
          <button
            type="button"
            className={`relative z-10 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${expanded ? 'text-blue-700 hover:bg-blue-100' : 'text-gray-600 hover:bg-gray-200/80'}`}
            aria-expanded={expanded}
            aria-controls={panelId}
            title={
              expanded
                ? t('btnCollapseHonorariumProject', { ns: NS, defaultValue: 'Replier le projet' })
                : t('btnExpandHonorariumProject', { ns: NS, defaultValue: 'Développer le projet' })
            }
            onClick={expandFromRow}
          >
            {expanded ? <FaChevronDown className="h-3.5 w-3.5" aria-hidden /> : <FaChevronRight className="h-3.5 w-3.5" aria-hidden />}
            <span className="sr-only">
              {expanded
                ? t('btnCollapseHonorariumProject', { ns: NS, defaultValue: 'Replier le projet' })
                : t('btnExpandHonorariumProject', { ns: NS, defaultValue: 'Développer le projet' })}
            </span>
          </button>
        </div>
      </td>
      <td
        className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1.5 align-middle text-left font-medium text-gray-800 ${projectColClass}`}
        onClick={expandFromRow}
      >
        <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} min-w-0 truncate font-semibold`} title={displayProject}>
          {displayProject}
        </div>
      </td>
      <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1.5 align-middle text-left ${customerColClass}`} onClick={expandFromRow}>
        <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} min-w-0 truncate`} title={noProject ? undefined : clientLabel}>
          {noProject ? '—' : clientLabel}
        </div>
      </td>
      <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1.5 align-middle text-left ${naturesColClass}`} onClick={expandFromRow}>
        <div
          className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} min-w-0 truncate tabular-nums`}
          title={naturesLabel || undefined}
        >
          {naturesLabel}
        </div>
      </td>
      {showSubProjectColumn ? (
        <td className={`${U3129_HONORARIUM_META_COLUMN_CELL_X} py-1.5 align-middle text-left ${subprojectColClass}`} onClick={expandFromRow}>
          <div className={U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS}>—</div>
        </td>
      ) : null}
      <td className={`${U3129_HONORARIUM_TASK_ORPHAN_HEADER_CLASS} p-0 border-0 py-1.5 align-middle`} aria-hidden="true" />
      {visibleDays.map((d) => (
        <td key={d} className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${hourColClass} align-middle text-right`} onClick={expandFromRow}>
          <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums font-medium text-gray-800`}>
            {formatDayHours(group.totals.daySums[d] ?? 0)}
          </div>
        </td>
      ))}
      <td className={`${U3129_GRID_COMPACT_ROW_HOUR_TD_BASE} ${hourColClass} align-middle text-right`} onClick={expandFromRow}>
        <div className={`${U3129_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} justify-end tabular-nums font-semibold text-gray-900`}>
          {formatTotalHours(group.totals.grandTotal)}
        </div>
      </td>
    </tr>
  );
};

export default U3129HonorariumProjectHeaderRow;
